"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import type { LucideIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface SidebarNavItemProps {
  icon: LucideIcon;
  label: string;
  href: string;
  onNavigate?: () => void;
  matchMode?: "exact" | "prefix";
  collapsed?: boolean;
  badge?: number;
}

export function SidebarNavItem({
  icon: Icon,
  label,
  href,
  onNavigate,
  matchMode = "prefix",
  collapsed = false,
  badge,
}: SidebarNavItemProps) {
  const pathname = usePathname();
  const isActive =
    matchMode === "exact"
      ? pathname === href
      : pathname === href || pathname.startsWith(`${href}/`);

  return (
    <Button
      asChild
      variant="ghost"
      onClick={onNavigate}
      className={cn(
        "group relative flex w-full items-center justify-start gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300 ease-out",
        "hover:-translate-y-px hover:bg-primary/10 hover:text-foreground hover:shadow-sm",
        collapsed && "md:justify-center md:px-2 md:gap-0",
        isActive
          ? "bg-primary/15 text-primary shadow-xs backdrop-blur-sm"
          : "text-muted-foreground",
      )}
    >
      <Link href={href} title={collapsed ? undefined : label} aria-label={label}>
        <Icon
          className={cn(
            "h-4 w-4 shrink-0 transition-transform duration-300 ease-out",
            "group-hover:scale-110",
          )}
        />
        <span
          className={cn(
            "flex flex-1 items-center justify-between transition-all duration-300 ease-out",
            collapsed && "md:hidden",
          )}
        >
          {label}
          {badge ? (
            <span className="ml-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-primary-foreground">
              {badge > 99 ? "99+" : badge}
            </span>
          ) : null}
        </span>
        {collapsed && badge ? (
          <span className="absolute -right-1 -top-1 hidden h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold leading-none text-primary-foreground md:flex">
            {badge > 99 ? "99+" : badge}
          </span>
        ) : null}
        {collapsed && (
          <span
            className="pointer-events-none absolute left-full top-1/2 z-[60] ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-popover px-2 py-1 text-xs font-medium text-popover-foreground opacity-0 shadow-md ring-1 ring-border transition-opacity duration-150 group-hover:opacity-100 md:block"
          >
            {label}
          </span>
        )}
      </Link>
    </Button>
  );
}
